import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import { EFFORT_HARD_SET_RIR, effectiveRir } from "./effortScale.js";
import type { LogSet } from "./schemas/logSet.js";
import {
  isCountedLogSet,
  NOMINAL_COUNTED_BODYWEIGHT_KG,
  type SetLoadLookup,
} from "./setMetrics.js";
import {
  type AggregatorExerciseRow,
  SUBSTANTIAL_MUSCLE_CONTRIB_MIN,
  resolveExerciseMusclesAbove,
} from "./volumeByMuscle.js";

// #522: the ONE rule for "this muscle was trained on this day", shared by the
// logged-session check (API sessionWarnings) and the program check
// (programMuscleAdjacency). A muscle counts on a day only with at least
// `minSets` DIRECT hard sets: sets at RIR <= EFFORT_HARD_SET_RIR on an
// exercise whose contribution to the muscle is >= SUBSTANTIAL_MUSCLE_CONTRIB_MIN.
// A synergist, a warm-up or a single set does not put the muscle on the day.

/** Direct hard sets a muscle needs on one day to count there. */
export const DEFAULT_MIN_DIRECT_HARD_SETS = 2;

/** Hard sets per exercise slug from LOGGED sets: a counted set (the same
 *  predicate as ExerciseResult.setCount, isCountedLogSet, which also drops
 *  cardio) at an effective RIR <= EFFORT_HARD_SET_RIR. Effort-unknown sets do
 *  NOT count as hard here (the warning discards them by choice), whereas
 *  sessionResult's top set still considers them. */
export const loggedHardSetsBySlug = (
  sets: ReadonlyArray<LogSet>,
  loadOf: (slug: string) => SetLoadLookup | undefined = () => undefined,
): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const s of sets) {
    const rir = effectiveRir(s);
    if (rir === null || rir > EFFORT_HARD_SET_RIR) continue;
    // Nominal bodyweight (the caller has none): exact for assistance below it.
    if (!isCountedLogSet(s, loadOf(s.exerciseSlug), NOMINAL_COUNTED_BODYWEIGHT_KG)) continue;
    out[s.exerciseSlug] = (out[s.exerciseSlug] ?? 0) + 1;
  }
  return out;
};

/** Muscles with at least `minSets` direct hard sets, given the hard sets
 *  done (or prescribed) per exercise slug that day. */
export const musclesWithDirectHardSets = (
  hardSetsBySlug: Readonly<Record<string, number>>,
  exerciseBySlug: ReadonlyMap<string, AggregatorExerciseRow>,
  minSets: number = DEFAULT_MIN_DIRECT_HARD_SETS,
): Set<CatalogMuscleSlug> => {
  const total = new Map<CatalogMuscleSlug, number>();
  for (const [slug, count] of Object.entries(hardSetsBySlug)) {
    const ex = exerciseBySlug.get(slug);
    if (!ex || count <= 0) continue;
    for (const m of resolveExerciseMusclesAbove(ex, SUBSTANTIAL_MUSCLE_CONTRIB_MIN))
      total.set(m, (total.get(m) ?? 0) + count);
  }
  const out = new Set<CatalogMuscleSlug>();
  for (const [m, n] of total) if (n >= minSets) out.add(m);
  return out;
};
